import type { Shadows } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';

/**
 * Shadow / elevation tokens, mirrored from Figma Effect Styles `elevation/1|8|16|24`
 * (Design System 2.0). Each level is a 3-layer Material shadow — umbra (20%) + penumbra (14%)
 * + ambient (12%), all black, X=0 — the same ladder MUI ships, now formalised as brand tokens.
 *
 * The design system standardises on FIVE elevation levels (see docs/DECISIONS.md):
 *   0  none    — flat, in-flow surfaces + all controls (Card, Table, inputs, chips, buttons…)
 *   1  raised  — bare `Paper` resting surface
 *   8  overlay — transient anchored overlays: Menu, Select, Autocomplete popup, pickers, dropdowns
 *   16 sheet   — sliding side sheets: Drawer
 *   24 modal   — the modal focus layer: Dialog
 */

// Named brand elevation tokens (mirrored 1:1 from the Figma Effect Styles). Referenced directly
// by the overlay `paper` slots in theme/components.ts and via the `shadows` ladder below.
const elevation1 = '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)';
const elevation8 =
  '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)';
const elevation16 =
  '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)';
const elevation24 =
  '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)';

export const shadowTokens = {
  none: 'none',
  elevation1,
  elevation8,
  elevation16,
  elevation24,
} as const;

export type ShadowTokens = typeof shadowTokens;

/**
 * MUI's `theme.shadows` is a fixed 25-entry ladder (elevation 0–24) that components read from
 * when given an `elevation` prop. We keep all 25 entries valid but source our four brand levels
 * (1/8/16/24) from `shadowTokens`, so bare `<Paper elevation={n}>` and the overlay slots share a
 * single source of truth. The remaining indices stay at MUI's defaults (unused by our 5-level scale).
 */
const base = createTheme().shadows;
export const shadows: Shadows = base.map((value, index) => {
  switch (index) {
    case 1:
      return shadowTokens.elevation1;
    case 8:
      return shadowTokens.elevation8;
    case 16:
      return shadowTokens.elevation16;
    case 24:
      return shadowTokens.elevation24;
    default:
      return value;
  }
}) as Shadows;
